import { mediaTypes } from '@/helpers/constants'
import { reactive } from 'vue'
import bus from '@/helpers/bus'
import { createLogger } from '@/helpers/log'

export const FILE_PROGRESS_UPLOAD_RATIO = 1
export const PHOTO_PROGRESS_UPLOAD_RATIO = 0.8
export const PHOTO_PROGRESS_NORMALIZATION_RATIO =
	1 - PHOTO_PROGRESS_UPLOAD_RATIO
export const VIDEO_PROGRESS_UPLOAD_RATIO = 0.5
export const VIDEO_PROGRESS_TRANSCODE_RATIO = 1 - VIDEO_PROGRESS_UPLOAD_RATIO
export const MESSAGE_PROGRESS_UNSENT_RATIO = 0.95

const log = createLogger('UPLOADS')

const uploadsById = reactive({})
const blobsById = reactive({})

export const setUpload = (id, data) => {
	if (!data || !id) return
	log('updating data for id:', id, data)
	const prev = uploadsById[id] || {}
	const merged = { ...prev, ...data }
	merged.progress = calcProgressForMedia(merged)
	merged.isComplete = merged.progress >= 100
	uploadsById[id] = merged
	blobsById[merged.uuid] = merged.blob
	if (merged.tempId) {
		blobsById[merged.tempId] = merged.blob
	}
	bus.$emit('upload-change', { ...merged, tempId: merged.tempId || id })
	return merged
}

export const setMediaUpload = (media, overrideObj = {}) => {
	if (!media) return
	const { tempId, uuid, mediatype, file, mediaUrl, messageUUID } = media
	setUpload(tempId, {
		tempId,
		uuid,
		mediatype,
		file,
		blob: mediaUrl,
		messageUUID,
		...overrideObj,
	})
}

export const getUpload = (id) => {
	return uploadsById[id] || null
}

export const getBlob = (id) => {
	return blobsById[id] || null
}

export const deleteUpload = (id) => {
	const upload = getUpload(id)
	if (!upload) return false
	delete uploadsById[upload.tempId]
	delete uploadsById[upload.uuid]
	delete blobsById[upload.tempId]
	delete blobsById[upload.uuid]
	return true
}

export const getUploadsForMessageUUID = (messageUUID) => {
	if (!messageUUID) return []
	return Object.values(uploadsById).filter((u) => u.messageUUID === messageUUID)
}

export const deleteUploadsForMessageUUID = (messageUUID) => {
	log('deleting uploads for message...', messageUUID)
	return getUploadsForMessageUUID(messageUUID).map((u) => deleteUpload(u.uuid))
}

// **********************************
// Progress Calculators
// **********************************

export const calcTotalProgressForMessageUUID = (messageUUID) => {
	if (!messageUUID) return 0
	const uploads = getUploadsForMessageUUID(messageUUID)
	if (!uploads.length) return 0
	const sum = uploads.reduce((sum, u) => sum + (u.progress || 0), 0)
	const totalPossible = 100 * uploads.length
	const totalProgress = Math.floor((sum / totalPossible) * 100)
	log('calculated progress for message', messageUUID, {
		totalProgress,
	})
	return totalProgress
}

export const calcProgressForMedia = (media) => {
	if (!media) return 0
	switch (media.mediatype) {
		case mediaTypes.PHOTO:
			return calcProgressForPhoto(media)
		case mediaTypes.FILE:
			return calcProgressForFile(media)
		case mediaTypes.VIDEO:
			return calcProgressForVideo(media)
		default:
			return 0
	}
}

export const calcProgressForFile = (media) => {
	// upload progress counts for 100% of total progress
	const { uploadProgress } = media
	return Math.floor((uploadProgress || 0) * FILE_PROGRESS_UPLOAD_RATIO)
}

export const calcProgressForPhoto = (media) => {
	// upload progress counts for 80% of total progress
	const { uploadProgress } = media
	const uploadPct = Math.floor(
		(uploadProgress || 0) * PHOTO_PROGRESS_UPLOAD_RATIO
	)
	// normalization counts for 20% of total progress
	const normalizationPct = media.isNormalized
		? 100 * PHOTO_PROGRESS_NORMALIZATION_RATIO
		: 0
	const totalProgress = Math.floor(uploadPct + normalizationPct)
	return totalProgress
}

export const calcProgressForVideo = (media) => {
	// upload progress counts for 80% of total progress
	const { uploadProgress, transcodeProgress } = media
	const uploadPct = Math.floor(
		(uploadProgress || 0) * VIDEO_PROGRESS_UPLOAD_RATIO
	)
	// normalization counts for 20% of total progress
	const transPct = (transcodeProgress || 0) * VIDEO_PROGRESS_TRANSCODE_RATIO
	const totalProgress = Math.floor(uploadPct + transPct)
	return totalProgress
}

export const uploadData = {
	uploadsById,
	blobsById,
	calcTotalProgressForMessageUUID,
}
